import type { CSSProperties } from 'react';
import React, { memo, useContext, useEffect, useRef, useState } from 'react';
import { useStickyNav } from '@/app/common/components/StickyNavBar/StickyNavContext.tsx';
import type { MapRef } from 'react-map-gl/mapbox';
import { useCommonClient } from '@/app/common/commonClientProvider.tsx';
import { usePageWrapperData } from '@/app/common/pageWrapperProvider.tsx';
import MapComponent, {
  type InitialHighlightHotel,
} from '@/app/components/search/components/SearchPageContainer/MapContainer/MapComponent';
import useIsClient from '@/app/components/search/hooks/useIsClient.ts';
import { SearchPageContext } from '@/app/components/search/components/SearchPageContainer/SearchPageContext.ts';
import { LAYOUT_TYPE } from '@/app/components/search/components/LayoutManager/utils.ts';
import { createNewSearchParamsBase } from '@/app/components/search/common/reloadData/createNewSearchParams.ts';
import { debounce } from '@/utils/debounce.ts';
import getSearchParams from '@/app/components/search/common/getSearchParams';
import { URLParameters } from '@/app/common/urlParameters';
import { FiltersBarContainerMobile } from '@/app/components/search/components/SearchPageContainer/ListContainer/styled.ts';
import { useIconColorHex } from '@/app/common/icons/Icon';
import { starfishCssUrl } from '@/app/common/icons/starfish';
import type { DynamicContentResponse } from '../../../utils/api/filtersAPIResponseTypes';
import { MapContainerStyled, ZOOM_ICON_SIZE } from './styled';

const RESIZE_THRESHOLD_PX = 32;

type Size = { width: number; height: number };

const isSignificantResize = (prev: Size, next: Size) =>
  Math.abs(prev.width - next.width) > RESIZE_THRESHOLD_PX || Math.abs(prev.height - next.height) > RESIZE_THRESHOLD_PX;

export type MapContainerProps = {
  mapRef: React.RefObject<MapRef | null>;
  showFilters: boolean;
  isLoading: boolean;
  isWaiting: boolean;
  cardOverHotelId: number | undefined;
  dynamicContentResponse?: DynamicContentResponse;
  forceFullscreenMapDesktop?: boolean;
  showCloseResize?: boolean;
  // The map fills a dialog of its own (the hotel-detail map), which lays its Evo controls out differently.
  isDialog?: boolean;
  initialHighlightHotel?: InitialHighlightHotel;
  shouldLaunchSearchChanged: boolean;
};

const MapContainer = memo(
  ({
    mapRef,
    showFilters,
    isLoading,
    isWaiting,
    cardOverHotelId,
    dynamicContentResponse,
    forceFullscreenMapDesktop,
    showCloseResize,
    isDialog,
    initialHighlightHotel,
    shouldLaunchSearchChanged,
  }: MapContainerProps) => {
    const isClient = useIsClient();
    const { localization } = usePageWrapperData();
    const { brand } = localization;
    const { isMobile, externalHeaderEnabled, handleSearchChanged, isEvoLayout } = useCommonClient();
    const mapContainerClassName = `map-container ${isEvoLayout ? 'map-container-evolution' : ''}`;
    const [isMapOnTop, setIsMapOnTop] = useState(false);
    const { layoutType } = useContext(SearchPageContext);
    const mapContainerRef = useRef<HTMLDivElement>(undefined);
    const iconPrimaryHex = useIconColorHex('iconPrimary');
    const { isScrolled } = useStickyNav();
    const [expand, setExpand] = useState<boolean>(false);
    const [containerStyle, setContainerStyle] = useState<CSSProperties>({});
    const renderMap =
      layoutType === LAYOUT_TYPE.DESKTOP_MAP || layoutType === LAYOUT_TYPE.MOBILE_MAP || forceFullscreenMapDesktop;

    // Mapbox's zoom buttons take a background-image, so the icon has to arrive as a
    // URL rather than an element — and the colour and drawn size with it.
    const zoomInIcon = starfishCssUrl('Semantics', 'icon-plus', iconPrimaryHex, ZOOM_ICON_SIZE);
    const zoomOutIcon = starfishCssUrl('Semantics', 'icon-minus', iconPrimaryHex, ZOOM_ICON_SIZE);

    useEffect(() => {
      if (isClient && mapContainerRef.current && isMobile) {
        const { height } = mapContainerRef.current.getBoundingClientRect();
        setContainerStyle({ height, minHeight: height, maxHeight: height });
      }
    }, [isClient, mapContainerRef]);

    const previousSizeRef = useRef<Size | null>(null);
    const isLoadingRef = useRef(isLoading);
    const isWaitingRef = useRef(isWaiting);
    isLoadingRef.current = isLoading;
    isWaitingRef.current = isWaiting;

    useEffect(() => {
      // Only re-launch a search on resize when map changes should drive the search
      // (results page). On the hotel-detail map (shouldLaunchSearchChanged=false) a
      // resize must not navigate away to the results page.
      if (!renderMap || !shouldLaunchSearchChanged) return undefined;

      const debounced = debounce(() => {
        // Prevent triggering during loading/waiting states to avoid infinite loops
        // Use refs to check fresh values at execution time, not closure time
        if (isLoadingRef.current || isWaitingRef.current) return;

        const searchParamsResetMap = getSearchParams({
          exclude: [URLParameters.MAP_AREA],
        });
        const newSearchParams = createNewSearchParamsBase({
          searchParams: searchParamsResetMap,
        });
        handleSearchChanged({ newSearchParams });
      }, 600);

      const resizeObserver = new ResizeObserver((entries) => {
        const entry = entries[0];
        if (!entry) return;

        const { width, height } = entry.contentRect;

        if (previousSizeRef.current === null) {
          previousSizeRef.current = { width, height };
          return;
        }

        if (isSignificantResize(previousSizeRef.current, { width, height })) {
          previousSizeRef.current = { width, height };
          debounced();
        }
      });

      if (mapContainerRef.current) {
        resizeObserver.observe(mapContainerRef.current);
      }

      return () => {
        debounced.cancel();
        resizeObserver.disconnect();
        previousSizeRef.current = null;
      };
    }, [renderMap, shouldLaunchSearchChanged]); // isLoading and isWaiting values are accessed via refs

    return (
      <MapContainerStyled
        ref={mapContainerRef}
        className={mapContainerClassName}
        $expand={expand}
        $zoomInIcon={zoomInIcon}
        $zoomOutIcon={zoomOutIcon}
        style={containerStyle}
        layoutType={layoutType}
        $isMapOnTop={isMapOnTop}
        data-scrolled={isScrolled}
        $forceFullscreenMapDesktop={forceFullscreenMapDesktop}
        $externalHeaderEnabled={externalHeaderEnabled}
        $brand={brand}
        evolutionResultsOnlyEnabled={isEvoLayout}
        $isDialog={isDialog}
      >
        {renderMap && (
          <MapComponent
            isWaiting={isWaiting}
            mapRef={mapRef}
            showFilters={showFilters}
            isLoading={isLoading}
            mapContainerRef={mapContainerRef}
            expand={expand}
            setExpand={setExpand}
            cardOverHotelId={cardOverHotelId}
            dynamicContentResponse={dynamicContentResponse}
            setIsMapOnTop={setIsMapOnTop}
            showCloseResize={showCloseResize}
            isDialog={isDialog}
            initialHighlightHotel={initialHighlightHotel}
            shouldLaunchSearchChanged={shouldLaunchSearchChanged}
          />
        )}
        {layoutType === LAYOUT_TYPE.MOBILE_MAP && <FiltersBarContainerMobile className="filters-bar-container" />}
      </MapContainerStyled>
    );
  },
  (prevProps: MapContainerProps, nextProps: MapContainerProps) => {
    if (prevProps.dynamicContentResponse !== nextProps.dynamicContentResponse) return false;
    if (prevProps.showFilters !== nextProps.showFilters) return false;
    if (prevProps.isLoading !== nextProps.isLoading) return false;
    if (prevProps.isWaiting !== nextProps.isWaiting) return false;
    if (prevProps.cardOverHotelId !== nextProps.cardOverHotelId) return false;
    if (prevProps.forceFullscreenMapDesktop !== nextProps.forceFullscreenMapDesktop) return false;
    return true;
  }
);

MapContainer.displayName = 'MapContainer';

export default MapContainer;
